import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  cloneDeep as _cloneDeep,
  isEqual as _isEqual,
} from 'lodash';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from "@mui/material/FormControl";
import Checkbox from '@mui/material/Checkbox';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import { makeStyles } from "@mui/styles";
import Auth from "../../../../data/auth-service";
import { isDuration } from "moment";

const initialBackendState = {
  groupName: "",
  group: "",
  balancingMode: "CONNECTION",
  failover: false,
  onCreateUpdate: true
};

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(1),
  },
  addButton: {
    float: 'right',
  },
  select: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  buttonRight: {
    float: 'right',
  },
  container: {
    border: '1px solid #515151',
    borderRadius: '10px',
    boxSizing: 'border-box',
  }
}));

const BackendConfig = ({ addBackend, updateBackend, editingBackend, instanceGroups, region, backendsList, onClose, isUpdate }) => {
  const classes = useStyles();
  const [backend, setBackend] = useState(initialBackendState);
  const [ogbackend, setOGbackend] = useState(initialBackendState);
  const [change, setChange] = useState(false);
  const [stackType, setStackType] = useState("single");
  const gcpPerms = Auth.permissions.get('GCP');
  const [readonly] = useState(!gcpPerms.write);
  const [groupName, setGroupName] = useState("");
  const [checkFailoverPolicy, setCheckFailoverPolicy] = useState(false);
  const instanceGroupList = instanceGroups.length > 0 ? instanceGroups.filter((group) => {
    if (group.subnetwork) {
      let subnetworkUrl = group.subnetwork;
      let urlParts = subnetworkUrl.split('/');
      let regionName = urlParts[urlParts.length - 3];
      return region === regionName;
    }
  }) : [];

  const [isValid, setIsValid] = useState(false);

  useEffect(() => {

    const valid = backend.group.length > 0;

    setChange(!_isEqual(backend, ogbackend));
    setIsValid(valid && !_isEqual(backend, ogbackend));
    
  }, [groupName, backend, checkFailoverPolicy]);

  useEffect(() => {
    if (editingBackend) {
      setBackend(editingBackend);
      setOGbackend(editingBackend);
      setGroupName(editingBackend.groupName);
    } else {
      setBackend(initialBackendState);
    }
  }, [editingBackend]);

  const handleAutocompleteChange = (event, newValue, field) => {
    if (newValue) {
      if (field === 'group') {
        setGroupName(newValue.name);
        var fieldName = field + 'Name';
        setBackend((prevData) => ({
          ...prevData,
          [fieldName]: newValue.name,
          [field]: newValue.selfLink || "",
        }));
      }
    }
  };

  const handleClose = () => {
    setBackend(initialBackendState);
    setIsValid(false);
    if (onClose) onClose();
  };


  const handleReset = () => {
    setBackend(initialBackendState);
    setIsValid(false);
  }

  const handleEnableFailover = (event) => {
    const isChecked = event.target.checked;
    setCheckFailoverPolicy(isChecked);
    setBackend((prevBackend) => ({
      ...prevBackend,
      failover: isChecked,
    }));
  };

  const handleSubmit = () => {
    let y = '';
    if (checkFailoverPolicy) {
      y = {
        disableConnectionDrainOnFailover: false,
        dropTrafficIfUnhealthy: false,
        failoverRatio: 0
      };
    } else if (!checkFailoverPolicy && isUpdate) {
      y = {
        disableConnectionDrainOnFailover: null,
        dropTrafficIfUnhealthy: null,
        failoverRatio: null
      };
    }

    let h = { ...backend, failoverPolicy: y, onCreateUpdate: true, stackType: stackType };
    if (editingBackend) {
      updateBackend(h);
    } else {
      addBackend(h);
    }
    setBackend(initialBackendState);
    setIsValid(false);
  
    setCheckFailoverPolicy(false);
  };

  const handleStackTypeChange = (event) => {
    setStackType(event.target.value);
  };

  return (

    <Grid container item xs={12}>
      <Grid item xs={12}>
        {editingBackend === null ?
          <Typography variant="h6">Add New Backend</Typography> :
          <Typography variant="h6">Update Backend</Typography>
        }
      </Grid>
      <Grid item xs={12} mt={2} className={classes.container}>
        <Box p={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset" margin="normal">
              <FormLabel>IP Stack Type</FormLabel>
              <RadioGroup row value={stackType} onChange={handleStackTypeChange}>
                <FormControlLabel disabled={editingBackend === null ? false : backend?.onCreateUpdate ? false : isUpdate} value="single" control={<Radio />} label="IPv4 (single-stack)" />
                <FormControlLabel disabled={editingBackend === null ? false : backend?.onCreateUpdate ? false : isUpdate} value="dual" control={<Radio />} label="IPv4 and IPv6 (dual-stack)" />
              </RadioGroup>
            </FormControl>
          </Grid>

          {stackType === "dual" ? (
            <Grid item xs={12}>
              <Grid item xs={6}>
                <Autocomplete
                  disabled={editingBackend === null ? false : backend?.onCreateUpdate ? false : isUpdate}
                  value={backend.groupName}
                  onChange={(event, newValue) =>
                    handleAutocompleteChange(
                      event,
                      newValue ? typeof newValue === "object" ? newValue : null : null,
                      "group"
                    )
                  }
                  options={instanceGroupList}
                  isOptionEqualToValue={(option, value) => option.name === value}
                  getOptionLabel={(option) =>
                    typeof option === "object" ? `${option.name}` : option
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="InstanceGroup" />
                  )}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid item container xs={12}>
              <Grid item xs={6}>
                <Autocomplete
                  disabled={editingBackend === null ? false : backend?.onCreateUpdate ? false : isUpdate}
                  value={backend.groupName}
                  onChange={(event, newValue) =>
                    handleAutocompleteChange(
                      event,
                      newValue ? (typeof newValue === "object" ? newValue : null) : null,
                      "group"
                    )
                  }
                  options={instanceGroupList}
                  isOptionEqualToValue={(option, value) => option.name === value}
                  getOptionLabel={(option) =>
                    typeof option === "object" ? `${option.name}` : option
                  }
                  getOptionDisabled={(option) =>
                    backendsList.some((backend) => backend.groupName === option.name)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="InstanceGroup" />
                  )}
                />

              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>

            <FormControlLabel
              control={
                <Checkbox
                  checked={backend.failover}
                  onChange={handleEnableFailover}
                  name="failover"
                />
              }
              label="Use this instance group as a failover group for backup"
            />
          </Grid>
          <Grid item xs={12} mt={2} container justifyContent="flex-end">
            {!readonly && (<Box>
              {editingBackend ? (
                <Button
                  variant="contained"
                  color="secondary"
                  className={classes.button}
                  onClick={handleClose}
                >
                  Cancel
                </Button>

              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={handleReset}
                >
                  Clear
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={(() => {
                  if (!change) {
                    return true;
                  }
                  if (change && isValid) {
                    return false;
                  }

                  return true;
                })()}
              >
                {isUpdate && editingBackend !== null ? 'Update' : editingBackend ? 'Update' : 'Add New Backend'}
              </Button>
            </Box>)}

          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
}

export default BackendConfig;
